<template>
  <div class="page">
    <h2 class="page-title">{{ $t('admin.systemManagement') }}</h2>

    <div class="glass-card" style="margin-top:14px; padding:20px;">
      <div style="max-width:600px;">
        <h3 style="margin:0 0 10px; font-size:16px; font-weight:600;">{{ $t('admin.purgeAllData') }}</h3>
        <div class="muted" style="font-size:13px; margin-bottom:16px;">
          {{ $t('admin.purgeWarning') }}
        </div>

        <el-form :model="form" :label-width="$i18n.locale === 'zh' ? '120px' : '180px'" size="small">
          <el-form-item :label="$t('admin.confirmPhrase')">
            <el-input
              v-model="form.confirmationPhrase"
              placeholder="DELETE ALL DATA"
              :disabled="purging"
            />
            <div class="muted" style="font-size:12px; margin-top:4px;">
              {{ $t('admin.confirmPhraseHint') }}
            </div>
          </el-form-item>

          <el-form-item :label="$t('admin.adminPassword')">
            <el-input
              v-model="form.adminPassword"
              type="password"
              :placeholder="$t('admin.adminPassword')"
              :disabled="purging"
              show-password
            />
          </el-form-item>

          <el-form-item>
            <el-button
              type="danger"
              :loading="purging"
              :disabled="!canPurge"
              @click="startPurge"
            >
              {{ purging ? $t('admin.purgeInProgress') : $t('admin.startPurge') }}
            </el-button>
          </el-form-item>
        </el-form>

        <div v-if="currentJob" class="purge-status">
          <div style="display:flex; align-items:center; gap:8px; margin-bottom:8px;">
            <span style="font-weight:600;">{{ $t('admin.purgeJobStatus') }}：</span>
            <span
              :class="{
                'tag-pill': true,
                'tag-pill--warn': currentJob.status === 'IN_PROGRESS',
                'tag-pill--danger': currentJob.status === 'FAILED'
              }"
            >
              {{ statusText(currentJob.status) }}
            </span>
          </div>

          <div v-if="currentJob.status === 'IN_PROGRESS'" style="margin-bottom:8px;">
            <el-progress :percentage="50" :show-text="false" />
            <div class="muted" style="font-size:12px; margin-top:4px;">
              {{ $t('admin.purgeDeleting') }}
            </div>
          </div>

          <div v-if="currentJob.deletedRecords > 0" class="muted" style="font-size:13px;">
            {{ $t('admin.purgeDeletedRecords', { count: currentJob.deletedRecords }) }}
          </div>

          <div v-if="currentJob.startedAt" class="muted" style="font-size:12px; margin-top:4px;">
            {{ $t('admin.purgeStartTime') }}：{{ formatTime(currentJob.startedAt) }}
          </div>

          <div v-if="currentJob.completedAt" class="muted" style="font-size:12px;">
            {{ $t('admin.purgeEndTime') }}：{{ formatTime(currentJob.completedAt) }}
          </div>

          <div v-if="currentJob.error" style="color:#ef4444; font-size:13px; margin-top:8px;">
            {{ $t('admin.purgeError') }}：{{ currentJob.error }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { adminPurgeAll, adminGetPurgeStatus } from '../../api/admin';

export default {
  name: 'AdminSystem',
  data() {
    return {
      form: {
        confirmationPhrase: '',
        adminPassword: ''
      },
      purging: false,
      currentJob: null,
      pollTimer: null
    };
  },
  computed: {
    canPurge() {
      return this.form.confirmationPhrase === 'DELETE ALL DATA' &&
             this.form.adminPassword &&
             !this.purging;
    }
  },
  beforeDestroy() {
    if (this.pollTimer) clearInterval(this.pollTimer);
  },
  methods: {
    async startPurge() {
      if (!this.canPurge) return;

      try {
        this.purging = true;
        const result = await adminPurgeAll({
          confirmationPhrase: this.form.confirmationPhrase,
          adminPassword: this.form.adminPassword
        });

        this.currentJob = {
          jobId: result.jobId,
          status: result.status,
          startedAt: new Date().toISOString(),
          deletedRecords: 0
        };

        this.startPolling(result.jobId);

        this.$message.success(this.$t('admin.purgeStarted'));
      } catch (e) {
        this.$message.error(e.response?.data?.message || this.$t('admin.purgeStartFailed'));
        this.purging = false;
      }
    },

    startPolling(jobId) {
      if (this.pollTimer) clearInterval(this.pollTimer);

      this.pollTimer = setInterval(async () => {
        try {
          const status = await adminGetPurgeStatus(jobId);
          this.currentJob = {
            ...this.currentJob,
            status: status.status,
            completedAt: status.completedAt || null,
            deletedRecords: status.deletedRecords || 0,
            error: status.error || null
          };

          if (status.status === 'COMPLETED' || status.status === 'FAILED') {
            clearInterval(this.pollTimer);
            this.pollTimer = null;
            this.purging = false;

            if (status.status === 'COMPLETED') {
              this.$message.success(this.$t('admin.purgeCompletedMsg', { count: status.deletedRecords }));
            } else {
              this.$message.error(this.$t('admin.purgeFailed'));
            }
          }
        } catch (e) {
          // Poll failure is non-critical, will retry on next interval
        }
      }, 2000);
    },

    statusText(status) {
      const map = {
        IN_PROGRESS: this.$t('admin.statusInProgress'),
        COMPLETED: this.$t('admin.statusCompleted'),
        FAILED: this.$t('admin.statusFailed')
      };
      return map[status] || status;
    },

    formatTime(iso) {
      if (!iso) return '';
      try {
        return new Date(iso).toLocaleString(this.$i18n.locale === 'zh' ? 'zh-CN' : 'en-US');
      } catch (e) {
        return iso;
      }
    }
  }
};
</script>

<style scoped>
.purge-status {
  margin-top: 20px;
  padding: 16px;
  border-radius: 12px;
  background: rgba(248, 250, 252, 0.9);
  border: 1px solid rgba(203, 213, 225, 0.8);
}

.action-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
}
</style>
